import React from 'react';
import { Helmet } from 'react-helmet-async';

const SEO = ({
  title = "Decadent Palete - Luxury Private Chef & Caribbean Soul Cuisine",
  description = "Experience exceptional Caribbean soul cuisine with our luxury private chef services. Authentic flavors, French techniques, and Indian spices create unforgettable culinary journeys.",
  keywords = "private chef, Caribbean cuisine, luxury dining, soul food, private dining, culinary experiences, wellness retreat, travel chef",
  image = "/images/og-image.jpg",
  url = "https://decadentpalete.com",
  type = "website",
  structuredData = null,
  noIndex = false,
  canonicalUrl = null
}) => {
  const siteTitle = "Decadent Palete";
  const fullTitle = title.includes(siteTitle) ? title : `${title} | ${siteTitle}`;
  const fullUrl = url.startsWith('http') ? url : `https://decadentpalete.com${url}`;
  const fullImageUrl = image.startsWith('http') ? image : `https://decadentpalete.com${image}`;
  const canonical = canonicalUrl || fullUrl;

  return (
    <Helmet>
      {/* Primary Meta Tags */}
      <title>{fullTitle}</title>
      <meta name="title" content={fullTitle} />
      <meta name="description" content={description} />
      <meta name="keywords" content={keywords} />
      
      {/* Robots */}
      {noIndex ? (
        <meta name="robots" content="noindex, nofollow" />
      ) : (
        <meta name="robots" content="index, follow" />
      )}
      
      {/* Canonical */}
      <link rel="canonical" href={canonical} />
      
      {/* Open Graph / Facebook */}
      <meta property="og:type" content={type} />
      <meta property="og:url" content={fullUrl} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={fullImageUrl} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:site_name" content={siteTitle} />
      
      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:url" content={fullUrl} />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={fullImageUrl} />
      <meta name="twitter:creator" content="@DecadentPalete" />
      
      {/* Structured Data */}
      {structuredData && (
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      )}
    </Helmet>
  );
};

// Predefined SEO configurations for different pages
export const seoConfigs = {
  home: {
    title: "Decadent Palete - Luxury Private Chef & Caribbean Soul Cuisine",
    description: "Experience exceptional Caribbean soul cuisine with our luxury private chef services. Book intimate private dining, wellness retreats, or culinary adventures with authentic flavors and French sophistication.",
    keywords: "luxury private chef, Caribbean soul cuisine, private dining, French technique, Indian spices, culinary experiences, wellness retreats",
    url: "/",
    structuredData: {
      "@context": "https://schema.org",
      "@type": "Restaurant",
      "name": "Decadent Palete",
      "description": "Luxury private chef services specializing in Caribbean soul cuisine",
      "servesCuisine": ["Caribbean", "Soul Food", "French", "Indian Fusion"],
      "priceRange": "$$$$",
      "acceptsReservations": true
    }
  },

  services: {
    title: "Caribbean Soul Cuisine Menu & Services - Decadent Palete",
    description: "Explore our exquisite Caribbean soul cuisine menu featuring authentic dishes with French techniques and Indian spice influences. Private dining experiences that celebrate heritage and flavor.",
    keywords: "Caribbean menu, soul food menu, private chef services, culinary menu, authentic Caribbean dishes, French technique, Indian spices",
    url: "/services",
    structuredData: {
      "@context": "https://schema.org",
      "@type": "Menu",
      "name": "Caribbean Soul Cuisine Menu",
      "description": "Authentic Caribbean dishes with French sophistication and Indian spice artistry",
      "hasMenuSection": [
        {
          "@type": "MenuSection",
          "name": "Appetizers",
          "description": "Caribbean soul-inspired starters with heritage spices"
        },
        {
          "@type": "MenuSection", 
          "name": "Main Courses",
          "description": "Signature dishes celebrating Caribbean traditions with French technique"
        },
        {
          "@type": "MenuSection",
          "name": "Desserts",
          "description": "Sweet endings with Caribbean soul and innovative presentation"
        }
      ]
    }
  },

  wellness: {
    title: "DP Lux Wellness - Caribbean Soul Cuisine Wellness Retreats",
    description: "Transform your relationship with food through our Caribbean soul-inspired wellness retreats. Experience healing cuisine, mindful cooking, and ancestral wellness wisdom.",
    keywords: "wellness retreat, Caribbean healing cuisine, mindful cooking, wellness programs, healing food, ancestral wisdom, nutrition",
    url: "/wellness-retreat",
    structuredData: {
      "@context": "https://schema.org",
      "@type": "HealthAndBeautyBusiness",
      "name": "DP Lux Wellness",
      "description": "Caribbean soul-inspired wellness retreats with healing cuisine",
      "serviceType": "Wellness Retreat",
      "hasOfferCatalog": {
        "@type": "OfferCatalog",
        "name": "Wellness Programs",
        "itemListElement": [
          {
            "@type": "Offer",
            "itemOffered": {
              "@type": "Service",
              "name": "Day Wellness Experience",
              "description": "Single day Caribbean soul wellness with nutrition consultation"
            }
          }
        ]
      }
    }
  },

  travel: {
    title: "Travel Culinary Adventures - Caribbean Heritage Food Tours",
    description: "Embark on extraordinary culinary adventures with your personal chef. Discover authentic Caribbean flavors, learn ancestral techniques, and explore heritage cuisine destinations.",
    keywords: "culinary travel, food tours, Caribbean heritage cuisine, culinary adventures, chef-guided tours, food experiences",
    url: "/travel-with-chef",
    structuredData: {
      "@context": "https://schema.org",
      "@type": "TouristTrip",
      "name": "Caribbean Culinary Adventures",
      "description": "Chef-guided culinary journeys exploring Caribbean heritage cuisine",
      "provider": {
        "@type": "Organization",
        "name": "Decadent Palete"
      },
      "includesAttraction": {
        "@type": "TouristAttraction",
        "name": "Caribbean Heritage Cuisine Experiences"
      }
    }
  },

  corporate: {
    title: "Corporate Catering & Private Events - Decadent Palete",
    description: "Elevate your corporate events with luxury Caribbean soul cuisine catering. Professional private chef services for meetings, conferences, and special corporate occasions.",
    keywords: "corporate catering, business events, private chef corporate, luxury catering, corporate dining, business meetings catering",
    url: "/corporate",
    structuredData: {
      "@context": "https://schema.org",
      "@type": "CateringBusiness",
      "name": "Decadent Palete Corporate Catering",
      "description": "Luxury corporate catering with Caribbean soul cuisine",
      "serviceType": "Corporate Catering",
      "areaServed": "Corporate Events"
    }
  },

  careers: {
    title: "Careers - Join Our Caribbean Soul Cuisine Team",
    description: "Join the Decadent Palete team and be part of our Caribbean soul cuisine family. Explore opportunities in culinary arts, wellness, and luxury hospitality services.",
    keywords: "culinary careers, chef jobs, wellness careers, hospitality jobs, Caribbean cuisine jobs, luxury service careers",
    url: "/careers",
    structuredData: {
      "@context": "https://schema.org",
      "@type": "JobPosting",
      "title": "Culinary Team Opportunities",
      "description": "Various positions available in Caribbean soul cuisine and luxury hospitality",
      "hiringOrganization": {
        "@type": "Organization",
        "name": "Decadent Palete"
      },
      "employmentType": ["FULL_TIME", "PART_TIME", "CONTRACT"]
    }
  },

  about: {
    title: "About Us - Caribbean Soul Cuisine Heritage & Philosophy",
    description: "Learn about our Caribbean heritage, culinary philosophy, and commitment to sharing authentic soul cuisine experiences. Meet our chef and discover our passion for food as cultural connection.",
    keywords: "Caribbean heritage, culinary philosophy, about chef, soul cuisine history, food culture, Caribbean traditions, culinary story",
    url: "/about",
    structuredData: {
      "@context": "https://schema.org",
      "@type": "AboutPage",
      "name": "About Decadent Palete",
      "description": "Our Caribbean heritage and culinary philosophy",
      "mainEntity": {
        "@type": "Organization",
        "name": "Decadent Palete",
        "foundingDate": "2020",
        "mission": "Sharing Caribbean soul cuisine heritage through exceptional culinary experiences"
      }
    }
  },

  booking: {
    title: "Book Your Caribbean Soul Cuisine Experience - Decadent Palete",
    description: "Reserve your luxury private chef experience. Choose from intimate private dining, wellness retreats, or culinary adventures. Book your Caribbean soul cuisine journey today.",
    keywords: "book private chef, reserve culinary experience, Caribbean cuisine booking, private dining reservation, wellness retreat booking",
    url: "/booking",
    structuredData: {
      "@context": "https://schema.org",
      "@type": "ReservationService",
      "name": "Decadent Palete Booking",
      "description": "Book luxury Caribbean soul cuisine experiences",
      "provider": {
        "@type": "Organization",
        "name": "Decadent Palete"
      }
    }
  },

  testimonials: {
    title: "Customer Reviews & Testimonials - Decadent Palete",
    description: "Read what our clients say about their Caribbean soul cuisine experiences. Authentic reviews from private dining guests, wellness retreat participants, and culinary adventure travelers.",
    keywords: "customer reviews, testimonials, Caribbean cuisine reviews, private chef reviews, wellness retreat testimonials",
    url: "/testimony",
    structuredData: {
      "@context": "https://schema.org",
      "@type": "Review",
      "itemReviewed": {
        "@type": "LocalBusiness",
        "name": "Decadent Palete"
      }
    }
  }
};

export default SEO;